import React, { useEffect } from "react";

interface TimeSelectProps {
  /** 24-hour "HH:mm" string, matching the native <input type="time"> value format */
  value: string;
  onChange: (value: string) => void;
  selectClassName?: string;
  disabled?: boolean;
}

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = [0, 15, 30, 45];

function parseValue(value: string): { hour12: number; minute: number; period: "AM" | "PM" } {
  if (!value) return { hour12: 12, minute: 0, period: "AM" };
  const [hStr, mStr] = value.split(":");
  const h24 = parseInt(hStr, 10) || 0;
  const rawMinute = parseInt(mStr, 10) || 0;
  const minute = MINUTES.reduce((closest, m) =>
    Math.abs(m - rawMinute) < Math.abs(closest - rawMinute) ? m : closest
  );
  const period: "AM" | "PM" = h24 >= 12 ? "PM" : "AM";
  const hour12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return { hour12, minute, period };
}

function toValue(hour12: number, minute: number, period: "AM" | "PM"): string {
  let h24 = hour12 % 12;
  if (period === "PM") h24 += 12;
  return `${String(h24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

/**
 * Replaces native <input type="time"> pickers, which can't reliably be
 * restricted to a fixed minute interval across browsers/OSes. Minutes are
 * hard-limited to :00/:15/:30/:45 via the <select> options themselves.
 */
const TimeSelect: React.FC<TimeSelectProps> = ({
  value,
  onChange,
  selectClassName = "",
  disabled,
}) => {
  const { hour12, minute, period } = parseValue(value);

  // The <select>s have no blank option, so they always visually show a
  // selected time (defaulting to 12:00 AM) even when `value` is still "" -
  // without this, the field looks filled in but the parent's state stays
  // empty, silently failing required-field checks (e.g. search-enabled).
  useEffect(() => {
    if (!value && !disabled) onChange(toValue(hour12, minute, period));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex items-center gap-1">
      <select
        aria-label="Hour"
        value={hour12}
        disabled={disabled}
        onChange={(e) => onChange(toValue(parseInt(e.target.value, 10), minute, period))}
        className={selectClassName}
      >
        {HOURS_12.map((h) => (
          <option key={h} value={h}>
            {String(h).padStart(2, "0")}
          </option>
        ))}
      </select>
      <span className="opacity-60">:</span>
      <select
        aria-label="Minute"
        value={minute}
        disabled={disabled}
        onChange={(e) => onChange(toValue(hour12, parseInt(e.target.value, 10), period))}
        className={selectClassName}
      >
        {MINUTES.map((m) => (
          <option key={m} value={m}>
            {String(m).padStart(2, "0")}
          </option>
        ))}
      </select>
      <select
        aria-label="AM/PM"
        value={period}
        disabled={disabled}
        onChange={(e) => onChange(toValue(hour12, minute, e.target.value as "AM" | "PM"))}
        className={selectClassName}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
};

export default TimeSelect;
